/**
 * Cuts a block of copy down to a fixed number of lines and offers a
 * "Mehr anzeigen" toggle — but only when there actually is more to show.
 *
 * Line count is set in CSS (`--clamp-lines`); this element just measures
 * whether the text overflows that limit and reveals the button if it does.
 * A short text therefore looks exactly as it would without the element.
 *
 * The clamp itself is keyed to `clamp-text:defined`, so without JavaScript the
 * full text shows and nothing is ever hidden behind a button that can't work.
 */

class ClampText extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-clamp-body]');
    this.button = this.querySelector('[data-clamp-toggle]');
    this.label = this.querySelector('[data-clamp-label]');

    if (!this.body || !this.button) return;

    this.onClick = () => this.#toggle();
    this.button.addEventListener('click', this.onClick);

    // Re-measure when the column changes width: the same text can fit on a wide
    // screen and overflow on a narrow one.
    this.observer = new ResizeObserver(() => this.#measure());
    this.observer.observe(this.body);

    this.#measure();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.button?.removeEventListener('click', this.onClick);
  }

  #measure() {
    // While open the text is unclamped, so it never overflows; measuring then
    // would hide the very button needed to close it again.
    if (this.hasAttribute('data-expanded')) return;

    const overflowing = this.body.scrollHeight > this.body.clientHeight + 1;
    this.toggleAttribute('data-overflowing', overflowing);
  }

  #toggle() {
    const expanded = this.toggleAttribute('data-expanded');
    this.button.setAttribute('aria-expanded', String(expanded));

    if (this.label) {
      this.label.textContent = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;
    }

    if (!expanded) this.#measure();
  }
}

if (!customElements.get('clamp-text')) {
  customElements.define('clamp-text', ClampText);
}
